import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Row, Col, Table, Spinner, Alert, Button, Badge } from 'react-bootstrap';
import { FiBarChart2, FiArrowLeft, FiMapPin, FiInfo, FiUser, FiCloudRain, FiNavigation, FiCalendar, FiCheckCircle, FiCloud } from 'react-icons/fi';
import { FaEuroSign } from 'react-icons/fa';
import { fetchTripObservationRequest } from '../../store/actions/tripsActions';
import PolicySumInsuredRows from '../../components/Common/PolicySumInsuredFields';
import 'bootstrap/dist/css/bootstrap.css';

const Label = ({ children }) => (
    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{children}</div>
);

const Value = ({ children }) => (
    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{children}</div>
);

const Section = ({ title, children }) => (
    <Card className="mb-3">
        <Card.Header style={{ fontWeight: 600 }}>{title}</Card.Header>
        <Card.Body>{children}</Card.Body>
    </Card>
);

const get = (obj, path, fallback = '—') => {
    try {
        const v = path.reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj);
        return v ?? fallback;
    } catch {
        return fallback;
    }
};

const AdminTripObservationDetail = () => {
    const { observationId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { isAuthenticated } = useSelector((state) => state.adminAuth);
    const observationState = useSelector((state) => state.trips.selectedObservation);

    useEffect(() => {
        if (isAuthenticated && observationId) {
            dispatch(fetchTripObservationRequest(observationId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, observationId]);

    const observation = useMemo(() => observationState?.data?.observation || null, [observationState]);

    const handleBack = () => {
        navigate(-1);
    };

    if (observationState.loading) {
        return (
            <div className="dashboard-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail Observation Trip</h2>
                            <Button variant="outline-secondary" onClick={handleBack}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Chargement du détail observation...</p>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (observationState.error) {
        return (
            <div className="dashboard-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail Observation Trip</h2>
                            <Button variant="outline-secondary" onClick={handleBack}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <Alert variant="danger">
                            {observationState.error}
                        </Alert>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (!observation) {
        return (
            <div className="dashboard-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail Observation Trip</h2>
                            <Button variant="outline-secondary" onClick={handleBack}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <p className="text-muted">Aucune donnée d'observation disponible pour l'identifiant: {observationId}.</p>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    return (
        <div className="dashboard-page">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="page-title">
                    <FiBarChart2 className="me-2" />
                    Détail Observation Trip
                </h2>
                <Button variant="outline-secondary" onClick={handleBack}>
                    <FiArrowLeft className="me-2" /> Retour
                </Button>
            </div>

            {/* Dernière observation */}
            {observation?.last_observation_info && observation.last_observation_info.last_observation_id && (
                <Section title="Dernière observation">
                    <Row className="mb-2">
                        <Col md={4}><Label>Source de données</Label><Value>{get(observation, ['last_observation_info', 'last_data_source'])}</Value></Col>
                        <Col md={4}><Label>Station</Label><Value>{get(observation, ['last_observation_info', 'last_station_name'])}</Value></Col>
                        <Col md={4}><Label>ID Station</Label><Value>{get(observation, ['last_observation_info', 'last_station_id'])}</Value></Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={4}><Label>Jour d'observation</Label><Value>{get(observation, ['last_observation_info', 'trip_day'])}</Value></Col>
                        <Col md={4}><Label>Date d'initialisation</Label><Value>
                            {get(observation, ['last_observation_info', 'last_observation_init_day'])}/
                            {get(observation, ['last_observation_info', 'last_observation_init_month'])}/
                            {get(observation, ['last_observation_info', 'last_observation_init_year'])}
                        </Value></Col>
                        <Col md={4}><Label>Heure d'exécution</Label><Value>{get(observation, ['last_observation_info', 'last_observation_run_hour'])}</Value></Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={4}><Label>Coordonnées station</Label><Value>
                            {get(observation, ['last_observation_info', 'last_latitude_station'])}, {get(observation, ['last_observation_info', 'last_longitude_station'])}
                        </Value></Col>
                        <Col md={4}><Label>Total mm précipitation</Label><Value>{get(observation, ['last_observation_info', 'last_total_mm_prc'], '0,0')}</Value></Col>
                        <Col md={4}><Label>Dernière modification</Label><Value>{get(observation, ['last_observation_info', 'last_observation_last_modified_at'])}</Value></Col>
                    </Row>
                    {observation.last_observation_info.last_observation_data && Array.isArray(observation.last_observation_info.last_observation_data) && observation.last_observation_info.last_observation_data.length > 0 && (
                        <div className="mt-3">
                            <Label>Données d'observation</Label>
                            <Table striped bordered hover size="sm" className="mt-2">
                                <thead>
                                    <tr>
                                        <th>Heure</th>
                                        <th>Précipitation (mm)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {observation.last_observation_info.last_observation_data.map((obs, idx) => (
                                        <tr key={idx}>
                                            <td>{obs.time || 'N/A'}</td>
                                            <td>{obs.prcp || '0,0'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Section>
            )}


            {/* Localisation / Activité */}
            <Section title="Localisation / Activité">
                <Row className="mb-2">
                    <Col md={4}><Label>Adresse</Label><Value><FiMapPin className="me-1" />{get(observation, ['activity_info', 'activity_address'])}</Value></Col>
                    <Col md={2}><Label>Ville</Label><Value>{get(observation, ['activity_info', 'activity_city'])}</Value></Col>
                    <Col md={2}><Label>Pays</Label><Value>{get(observation, ['activity_info', 'activity_country'])}</Value></Col>
                    <Col md={2}><Label>Latitude</Label><Value>{get(observation, ['activity_info', 'latitude'])}</Value></Col>
                    <Col md={2}><Label>Longitude</Label><Value>{get(observation, ['activity_info', 'longitude'])}</Value></Col>
                </Row>
            </Section>

            {/* Dates du séjour */}
            <Section title="Dates du séjour">
                <Row className="mb-2">
                    <Col md={4}><Label>Début activité</Label><Value><FiCalendar className="me-1" />{get(observation, ['activity_info', 'activity_start_date'])}</Value></Col>
                    <Col md={4}><Label>Fin activité</Label><Value><FiCalendar className="me-1" />{get(observation, ['activity_info', 'activity_end_date'])}</Value></Col>
                    <Col md={4}><Label>Début effectif</Label><Value><FiCalendar className="me-1" />{get(observation, ['activity_info', 'trip_effective_start_date'])}</Value></Col>
                    <Col md={4}><Label>Fin effective</Label><Value><FiCalendar className="me-1" />{get(observation, ['activity_info', 'trip_effective_end_date'])}</Value></Col>
                </Row>
            </Section>

            {/* Informations client */}
            <Section title="Informations client">
                <Row className="mb-2">
                    <Col md={4}><Label>Nom complet</Label><Value>{get(observation, ['client_info', 'first_name'])} {get(observation, ['client_info', 'last_name'])}</Value></Col>
                    <Col md={4}><Label>Email</Label><Value>{get(observation, ['client_info', 'email'])}</Value></Col>
                    <Col md={4}><Label>Téléphone</Label><Value>{get(observation, ['client_info', 'phone_number'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Code client</Label><Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{get(observation, ['client_info', 'client_code'])}</Value></Col>
                    <Col md={4}><Label>Statut</Label><Value>{get(observation, ['client_info', 'status'])}</Value></Col>
                    <Col md={4}><Label>Date d'inscription</Label><Value>{get(observation, ['client_info', 'date_joined'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Adresse</Label><Value>{get(observation, ['client_info', 'client_address'])}</Value></Col>
                    <Col md={2}><Label>Code postal</Label><Value>{get(observation, ['client_info', 'zip_code'])}</Value></Col>
                    <Col md={4}><Label>Ville</Label><Value>{get(observation, ['client_info', 'city'])}</Value></Col>
                    <Col md={4}><Label>Pays</Label><Value>{get(observation, ['client_info', 'country'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>IBAN reçu</Label><Value>{get(observation, ['client_info', 'is_iban_received']) ? 'Oui' : 'Non'}</Value></Col>
                    <Col md={4}><Label>IBAN</Label><Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{get(observation, ['client_info', 'iban_masked'])}</Value></Col>
                    <Col md={4}><Label>LCBFT vérifié</Label><Value><Badge bg={get(observation, ['client_info', 'lcbft_checked']) ? 'success' : 'secondary'}>{get(observation, ['client_info', 'lcbft_checked']) ? 'Oui' : 'Non'}</Badge></Value></Col>
                </Row>
            </Section>

            {/* Référence / Contrat */}
            <Section title="Référence / Contrat">
                <Row className="mb-2">
                    <Col md={4}><Label>Policy ID</Label><Value>{get(observation, ['policy_info', 'policy_id'], '—')}</Value></Col>
                    <Col md={4}><Label>Code policy</Label><Value>{get(observation, ['policy_info', 'policy_code'])}</Value></Col>
                    <Col md={4}><Label>Date UW</Label><Value>{get(observation, ['policy_info', 'uw_date'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Statut</Label><Value>{get(observation, ['policy_info', 'status'])}</Value></Col>
                    <Col md={4}><Label>Langue préférée</Label><Value>{get(observation, ['policy_info', 'preferred_language'])}</Value></Col>
                    <Col md={4}><Label>Envoi RIB</Label><Value>{get(observation, ['policy_info', 'date_rib_submission'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Nuits déclenchées</Label><Value>{get(observation, ['policy_info', 'nb_nights_triggered'])}</Value></Col>
                    <Col md={4}><Label>Nuits séjour</Label><Value>{get(observation, ['policy_info', 'trip_number_nights'])}</Value></Col>
                    <Col md={4}><Label>Nouv. nuits déclenchées</Label><Value>{get(observation, ['policy_info', 'new_nb_nights_triggered'])}</Value></Col>
                    <Col md={4}><Label>Nouv. nuits séjour</Label><Value>{get(observation, ['policy_info', 'new_trip_number_nights'])}</Value></Col>
                </Row>
                <Row>
                    <Col md={4}><Label>Ratio Orakle (HT)</Label><Value>{get(observation, ['policy_info', 'orakle_price_ratio_excl_tsca'])}</Value></Col>
                    <Col md={4}><Label>Prime assurance (HT TSCA)</Label><Value>{get(observation, ['policy_info', 'insurance_price_excl_tsca'])}</Value></Col>
                    <Col md={4}><Label>Prix TTC (incl. TSCA)</Label><Value>{get(observation, ['policy_info', 'price_incl_tsca'])}</Value></Col>
                    <Col md={4}><Label>Montant TSCA</Label><Value>{get(observation, ['policy_info', 'tsca_amount'])}</Value></Col>
                </Row>
                <Row className="mt-2">
                    <Col md={4}><Label>TSCA (%)</Label><Value>{get(observation, ['policy_info', 'tsca'])}</Value></Col>
                </Row>
                <PolicySumInsuredRows source={observation?.policy_info} Label={Label} Value={Value} colMd={6} />
            </Section>

            {/* Informations de partenaire */}
            <Section title="Informations de partenaire">
                <Row className="mb-2">
                    <Col md={4}><Label>Nom</Label><Value>{get(observation, ['partner_info', 'partner_first_name'])} {get(observation, ['partner_info', 'partner_last_name'])}</Value></Col>
                    <Col md={4}><Label>Entreprise</Label><Value>{get(observation, ['partner_info', 'company_name'])}</Value></Col>
                    <Col md={4}><Label>Email</Label><Value>{get(observation, ['partner_info', 'email'])}</Value></Col>
                </Row>
                <Row>
                    <Col md={4}><Label>Code</Label><Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{get(observation, ['partner_info', 'code'])}</Value></Col>
                    <Col md={4}><Label>Téléphone</Label><Value>{get(observation, ['partner_info', 'phone_number'])}</Value></Col>
                    <Col md={4}><Label>Commission</Label><Value>{get(observation, ['partner_info', 'commission'])}</Value></Col>
                </Row>
            </Section>

            {/* Informations de référence */}
            <Section title="Informations de référence">
                <Row className="mb-2">
                    <Col md={4}><Label>Nom référence</Label><Value>{get(observation, ['reference_info', 'reference_name'])}</Value></Col>
                    <Col md={4}><Label>Catégorie</Label><Value>{get(observation, ['reference_info', 'reference_category'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Code référence</Label><Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{get(observation, ['reference_info', 'reference_code'])}</Value></Col>
                    <Col md={4}><Label>Déclenchement cumulé</Label><Value>{get(observation, ['reference_info', 'prcp_is_cumulated']) ? 'Oui' : 'Non'}</Value></Col>
                </Row>
            </Section>

            {/* Vérification du sinistre */}
            <Section title="Vérification du sinistre">
                <Row className="mb-2">
                    <Col md={4}><Label>Observation déclenchée</Label><Value>
                        {(() => {
                            const isTriggeredObs = get(observation, ['CheckClaims', 'is_triggered_observation'], null);
                            if (isTriggeredObs === null) {
                                return <Badge bg="warning">En attente de traitement</Badge>;
                            }
                            return <Badge bg={isTriggeredObs ? 'success' : 'danger'}>{isTriggeredObs ? 'Oui' : 'Non'}</Badge>;
                        })()}
                    </Value></Col>
                    <Col md={4}><Label>Forecast déclenché</Label><Value>
                        {(() => {
                            const isTriggeredForecast = get(observation, ['CheckClaims', 'is_triggered_forecast'], null);
                            if (isTriggeredForecast === null) {
                                return <Badge bg="secondary">N/A</Badge>;
                            }
                            return <Badge bg={isTriggeredForecast ? 'success' : 'danger'}>{isTriggeredForecast ? 'Oui' : 'Non'}</Badge>;
                        })()}
                    </Value></Col>
                    <Col md={4}><Label>Forecast vérifié</Label><Value>{get(observation, ['CheckClaims', 'forecast_checked']) ? 'Oui' : 'Non'}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Trigger Low</Label><Value>{get(observation, ['CheckClaims', 'trigger_low'])}</Value></Col>
                    <Col md={4}><Label>Trigger Sup</Label><Value>{get(observation, ['CheckClaims', 'check_trigger_sup'])}</Value></Col>
                    <Col md={4}><Label>Heure inférieure</Label><Value>{get(observation, ['CheckClaims', 'prcp_trigger_hour_inf'])}</Value></Col>
                    <Col md={4}><Label>Heure supérieure</Label><Value>{get(observation, ['CheckClaims', 'trigger_hour_sup'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Nombre d'heures déclenchées</Label><Value>{get(observation, ['CheckClaims', 'count_hours_triggered'])}</Value></Col>
                    <Col md={4}><Label>Déclenchement cumulé</Label><Value>{get(observation, ['CheckClaims', 'prcp_is_cumulated']) ? 'Oui' : 'Non'}</Value></Col>
                    <Col md={4}><Label>Nombre d'heures trigger</Label><Value>{get(observation, ['CheckClaims', 'trigger_number_hours'])}</Value></Col>
                    <Col md={4}><Label>Observation vérifiée</Label><Value>{get(observation, ['CheckClaims', 'observation_checked']) ? 'Oui' : 'Non'}</Value></Col>
                </Row>
                <Row>
                    <Col md={4}><Label>Dernière modification</Label><Value>{get(observation, ['CheckClaims', 'check_claim_last_modified_at'])}</Value></Col>
                    <Col md={4}><Label>Modifié par</Label><Value>{get(observation, ['CheckClaims', 'check_claim_last_modified_by'])}</Value></Col>
                </Row>
            </Section>

            {/* Informations prévision */}
            {observation?.forecast_info && observation.forecast_info.forecast_id && (
                <Section title="Informations prévision">
                    <Row className="mb-2">
                        <Col md={4}><Label>ID Prévision</Label><Value>{get(observation, ['forecast_info', 'forecast_id'])}</Value></Col>
                        <Col md={4}><Label>Source de données</Label><Value>{get(observation, ['forecast_info', 'data_source']) || 'N/A'}</Value></Col>
                        <Col md={4}><Label>Jour du séjour</Label><Value>{get(observation, ['forecast_info', 'trip_day'])}</Value></Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={4}><Label>Coordonnées</Label><Value>
                            {get(observation, ['forecast_info', 'latitude'])}, {get(observation, ['forecast_info', 'longitude'])}
                        </Value></Col>
                        <Col md={4}><Label>Adresse</Label><Value>{get(observation, ['forecast_info', 'address'])}</Value></Col>
                        <Col md={4}><Label>Date de prévision</Label><Value>{get(observation, ['forecast_info', 'forecast_date']) || 'N/A'}</Value></Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={4}><Label>Total mm précipitation</Label><Value>{get(observation, ['forecast_info', 'total_mm_prc']) || 'N/A'}</Value></Col>
                        <Col md={4}><Label>Dernière modification</Label><Value>{get(observation, ['forecast_info', 'forecast_last_modified_at'])}</Value></Col>
                        <Col md={4}><Label>Modifié par</Label><Value>{get(observation, ['forecast_info', 'forecast_last_modified_by'])}</Value></Col>
                    </Row>
                </Section>
            )}

            {/* Sous-couverture Trip */}
            {observation?.subcoverage_trip_data && observation.subcoverage_trip_data.subcoverage_trip_id && (
                <Section title="Sous-couverture Trip">
                    <Row className="mb-2">
                        <Col md={4}><Label>ID Sous-couverture</Label><Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{get(observation, ['subcoverage_trip_data', 'subcoverage_trip_id'])}</Value></Col>
                        <Col md={4}><Label>Code sous-couverture</Label><Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{get(observation, ['subcoverage_trip_data', 'subcoverage_trip_code'])}</Value></Col>
                        <Col md={4}><Label>Type déclencheur</Label><Value>{get(observation, ['subcoverage_trip_data', 'trigger_type'])}</Value></Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={4}><Label>Trigger Low</Label><Value>{get(observation, ['subcoverage_trip_data', 'trigger_low'])}</Value></Col>
                        <Col md={4}><Label>Trigger Sup</Label><Value>{get(observation, ['subcoverage_trip_data', 'trigger_sup'])}</Value></Col>
                        <Col md={4}><Label>Unité</Label><Value>{get(observation, ['subcoverage_trip_data', 'unit'])}</Value></Col>
                        <Col md={4}><Label>Heure inférieure</Label><Value>{get(observation, ['subcoverage_trip_data', 'prcp_trigger_hour_inf'])}</Value></Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={4}><Label>Heure supérieure</Label><Value>{get(observation, ['subcoverage_trip_data', 'trigger_hour_sup'])}</Value></Col>
                        <Col md={4}><Label>Nombre d'heures trigger</Label><Value>{get(observation, ['subcoverage_trip_data', 'trigger_number_hours'])}</Value></Col>
                        <Col md={4}><Label>Heure début jour</Label><Value>{get(observation, ['subcoverage_trip_data', 'day_start_time'])}</Value></Col>
                        <Col md={4}><Label>Heure fin jour</Label><Value>{get(observation, ['subcoverage_trip_data', 'day_end_time'])}</Value></Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={4}><Label>SIV ratio maintenu</Label><Value>{get(observation, ['subcoverage_trip_data', 'siv_ratio_maintained'])}</Value></Col>
                        <Col md={4}><Label>SIV ratio annulé</Label><Value>{get(observation, ['subcoverage_trip_data', 'siv_ratio_cancelled'])}</Value></Col>
                        <Col md={4}><Label>Part cash maintien</Label><Value>{get(observation, ['subcoverage_trip_data', 'cash_share_maintain'])}</Value></Col>
                        <Col md={4}><Label>Part voucher maintien</Label><Value>{get(observation, ['subcoverage_trip_data', 'voucher_share_maintain'])}</Value></Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={4}><Label>Date début sous-couverture</Label><Value><FiCalendar className="me-1" />{get(observation, ['subcoverage_trip_data', 'sub_coverage_start_date'])}</Value></Col>
                        <Col md={4}><Label>Date fin sous-couverture</Label><Value><FiCalendar className="me-1" />{get(observation, ['subcoverage_trip_data', 'sub_coverage_end_date'])}</Value></Col>
                        <Col md={4}><Label>Date effective début</Label><Value><FiCalendar className="me-1" />{get(observation, ['subcoverage_trip_data', 'effective_start_date'])}</Value></Col>
                        <Col md={4}><Label>Date effective fin</Label><Value><FiCalendar className="me-1" />{get(observation, ['subcoverage_trip_data', 'effective_end_date'])}</Value></Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={4}><Label>Ville</Label><Value>{get(observation, ['subcoverage_trip_data', 'city'])}</Value></Col>
                        <Col md={2}><Label>Code postal</Label><Value>{get(observation, ['subcoverage_trip_data', 'zip_code'])}</Value></Col>
                        <Col md={4}><Label>Pays</Label><Value>{get(observation, ['subcoverage_trip_data', 'country'])}</Value></Col>
                        <Col md={4}><Label>Adresse activité</Label><Value>{get(observation, ['subcoverage_trip_data', 'activity_address'])}</Value></Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={4}><Label>Coordonnées géo</Label><Value>{get(observation, ['subcoverage_trip_data', 'geo_latitude'])}, {get(observation, ['subcoverage_trip_data', 'geo_longitude'])}</Value></Col>
                        <Col md={4}><Label>Ratio Orakle</Label><Value>{get(observation, ['subcoverage_trip_data', 'orakle_price_ratio_excl_tsca'])}</Value></Col>
                        <Col md={4}><Label>Type distribution</Label><Value>{get(observation, ['subcoverage_trip_data', 'distribution_type'])}</Value></Col>
                        <Col md={4}><Label>Plan de paiement</Label><Value>{get(observation, ['subcoverage_trip_data', 'payment_schedule'])}</Value></Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={4}><Label>Lump sum</Label><Value><Badge bg={get(observation, ['subcoverage_trip_data', 'is_lump_sum']) ? 'success' : 'secondary'}>{get(observation, ['subcoverage_trip_data', 'is_lump_sum']) ? 'Oui' : 'Non'}</Badge></Value></Col>
                        <Col md={4}><Label>Lump sum maintien</Label><Value>{get(observation, ['subcoverage_trip_data', 'lump_sum_maintain'])}</Value></Col>
                        <Col md={4}><Label>Lump sum annulation</Label><Value>{get(observation, ['subcoverage_trip_data', 'lump_sum_cancellation'])}</Value></Col>
                        <Col md={4}><Label>Source données maintien</Label><Value>{get(observation, ['subcoverage_trip_data', 'maintain_data_source'])}</Value></Col>
                    </Row>
                    <Row>
                        <Col md={4}><Label>Source données annulation</Label><Value>{get(observation, ['subcoverage_trip_data', 'cancellation_data_source'])}</Value></Col>
                        <Col md={4}><Label>Split voucher/cash maintien</Label><Value><Badge bg={get(observation, ['subcoverage_trip_data', 'split_voucher_cash_maintain']) ? 'success' : 'secondary'}>{get(observation, ['subcoverage_trip_data', 'split_voucher_cash_maintain']) ? 'Oui' : 'Non'}</Badge></Value></Col>
                        <Col md={4}><Label>Split voucher/cash annulation</Label><Value><Badge bg={get(observation, ['subcoverage_trip_data', 'split_voucher_cash_cancellation']) ? 'success' : 'secondary'}>{get(observation, ['subcoverage_trip_data', 'split_voucher_cash_cancellation']) ? 'Oui' : 'Non'}</Badge></Value></Col>
                    </Row>
                </Section>
            )}
        </div>
    );
};

export default AdminTripObservationDetail;




